package dev.lingvo.site.pages.app.projects.slug

import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.shared.StoreConnectionStatus
import dev.lingvo.shared.StoreDiffDto
import dev.lingvo.shared.StoreField
import dev.lingvo.shared.StoreFieldDiffDto
import dev.lingvo.shared.StoreFieldStatus
import dev.lingvo.shared.StoreKeywordPlanDto
import dev.lingvo.shared.StoreLocaleDiffDto
import dev.lingvo.shared.StoreLimitUnit
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StorePushStatus
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf

/*
 * The Stores screen's decisions, kept out of composition so they can be tested without a browser.
 *
 * Every label here is a `tr`, never a `t`: these run inside event handlers and plain functions as
 * well as inside composables, and the catalogue is fixed before the first frame either way.
 */

/** The error codes this screen explains in its own words rather than repeating the server's. */
internal const val STORE_VAULT_UNCONFIGURED = "store_vault_unconfigured"
internal const val STORE_DIFF_STALE = "store_diff_stale"
internal const val STORE_ROLLBACK_INCOMPLETE = "store_rollback_incomplete"

internal fun storeFieldLabel(field: StoreField): String = when (field) {
    StoreField.APP_NAME -> tr("app.projects.slug.stores_model.field_app_name", "Name")
    StoreField.APP_SUBTITLE -> tr("app.projects.slug.stores_model.field_subtitle", "Subtitle")
    StoreField.APP_DESCRIPTION -> tr("app.projects.slug.stores_model.field_description", "Description")
    StoreField.APP_PROMOTIONAL_TEXT -> tr("app.projects.slug.stores_model.field_promotional_text", "Promotional text")
    StoreField.APP_KEYWORDS -> tr("app.projects.slug.stores_model.field_keywords", "Keywords")
    StoreField.APP_SUPPORT_URL -> tr("app.projects.slug.stores_model.field_support_url", "Support URL")
    StoreField.APP_WHATS_NEW -> tr("app.projects.slug.stores_model.field_whats_new", "What's new")
    StoreField.PLAY_TITLE -> tr("app.projects.slug.stores_model.field_play_title", "Title")
    StoreField.PLAY_SHORT_DESCRIPTION -> tr("app.projects.slug.stores_model.field_short_description", "Short description")
    StoreField.PLAY_FULL_DESCRIPTION -> tr("app.projects.slug.stores_model.field_full_description", "Full description")
    StoreField.PLAY_RELEASE_NOTES -> tr("app.projects.slug.stores_model.field_release_notes", "Release notes")
}

internal fun storeFieldStatusLabel(status: StoreFieldStatus): String = when (status) {
    StoreFieldStatus.UNCHANGED -> tr("app.projects.slug.stores_model.status_unchanged", "Unchanged")
    StoreFieldStatus.CHANGED -> tr("app.projects.slug.stores_model.status_changed", "Will be pushed")
    StoreFieldStatus.NOT_REVIEWED -> tr("app.projects.slug.stores_model.status_not_reviewed", "Not reviewed")
    StoreFieldStatus.OVER_LIMIT -> tr("app.projects.slug.stores_model.status_over_limit", "Over the limit")
}

/** Colour follows consequence: only a refusal is red, and only unapproved text asks for attention. */
internal fun storeFieldStatusVariant(status: StoreFieldStatus): LBadgeVariant = when (status) {
    StoreFieldStatus.UNCHANGED -> LBadgeVariant.NEUTRAL
    StoreFieldStatus.CHANGED -> LBadgeVariant.INFO
    StoreFieldStatus.NOT_REVIEWED -> LBadgeVariant.WARNING
    StoreFieldStatus.OVER_LIMIT -> LBadgeVariant.DANGER
}

/** One field's length against its store limit, in the unit that store counts. */
internal data class StoreLengthMeter(
    val used: Int,
    val limit: Int,
    val unit: StoreLimitUnit,
) {
    val over: Boolean get() = limit > 0 && used > limit
    val remaining: Int get() = limit - used
}

/** Read from the diff rather than recomputed: the server measured the exact text it would push. */
internal fun storeLengthMeter(diff: StoreFieldDiffDto): StoreLengthMeter =
    StoreLengthMeter(used = diff.used, limit = diff.limit.takeIf { it > 0 } ?: diff.field.limit, unit = diff.unit)

/** `27/30 characters`, or `101/100 bytes` for Apple's keyword field, which is the one byte budget. */
internal fun storeLengthMeterText(meter: StoreLengthMeter): String = when (meter.unit) {
    StoreLimitUnit.CHARACTERS -> trf(
        "app.projects.slug.stores_model.meter_characters",
        "{used}/{limit} characters",
        "used" to meter.used,
        "limit" to meter.limit,
    )
    StoreLimitUnit.BYTES -> trf(
        "app.projects.slug.stores_model.meter_bytes",
        "{used}/{limit} bytes",
        "used" to meter.used,
        "limit" to meter.limit,
    )
}

internal fun storePushStatusLabel(status: StorePushStatus): String = when (status) {
    StorePushStatus.PENDING -> tr("app.projects.slug.stores_model.push_pending", "Sending")
    StorePushStatus.SUCCEEDED -> tr("app.projects.slug.stores_model.push_succeeded", "Live")
    StorePushStatus.PARTIAL -> tr("app.projects.slug.stores_model.push_partial", "Partly live")
    StorePushStatus.QUEUED -> tr("app.projects.slug.stores_model.push_queued", "Queued")
    StorePushStatus.FAILED -> tr("app.projects.slug.stores_model.push_failed", "Failed")
}

internal fun storePushStatusVariant(status: StorePushStatus): LBadgeVariant = when (status) {
    StorePushStatus.PENDING -> LBadgeVariant.NEUTRAL
    StorePushStatus.SUCCEEDED -> LBadgeVariant.SUCCESS
    StorePushStatus.PARTIAL -> LBadgeVariant.WARNING
    StorePushStatus.QUEUED -> LBadgeVariant.INFO
    StorePushStatus.FAILED -> LBadgeVariant.DANGER
}

/** The server writes `PUSH` or `ROLLBACK`; anything newer is shown as written rather than hidden. */
internal fun storePushKindLabel(kind: String): String = when (kind.uppercase()) {
    "PUSH" -> tr("app.projects.slug.stores_model.kind_push", "Push")
    "ROLLBACK" -> tr("app.projects.slug.stores_model.kind_rollback", "Rollback")
    else -> kind.lowercase().replaceFirstChar { it.uppercase() }
}

internal fun storeConnectionStatusLabel(status: StoreConnectionStatus): String = when (status) {
    StoreConnectionStatus.UNVERIFIED -> tr("app.projects.slug.stores_model.connection_unverified", "App not verified yet")
    StoreConnectionStatus.VERIFIED -> tr("app.projects.slug.stores_model.connection_verified", "Working")
    StoreConnectionStatus.REJECTED -> tr("app.projects.slug.stores_model.connection_rejected", "Access needs attention")
    StoreConnectionStatus.REVOKED -> tr("app.projects.slug.stores_model.connection_revoked", "Removed")
}

internal fun storeConnectionStatusVariant(status: StoreConnectionStatus): LBadgeVariant = when (status) {
    StoreConnectionStatus.UNVERIFIED -> LBadgeVariant.NEUTRAL
    StoreConnectionStatus.VERIFIED -> LBadgeVariant.SUCCESS
    StoreConnectionStatus.REJECTED -> LBadgeVariant.DANGER
    StoreConnectionStatus.REVOKED -> LBadgeVariant.NEUTRAL
}

/** The only part of a secret that ever reaches the page: its last four characters. */
internal fun storeCredentialHintText(connection: StoreConnectionDto): String = if (connection.identity["authType"] == "managed") tr("app.projects.slug.stores_model.keyless", "Keyless account") else trf(
    "app.projects.slug.stores_model.credential_hint",
    "ends in ••••{hint}",
    "hint" to connection.credentialHint,
)

/** A connection's non-secret identifiers, named for a person; an unknown one keeps its own name. */
internal fun storeIdentityLabel(key: String): String = when (key) {
    "issuerId" -> tr("app.projects.slug.stores_model.identity_issuer_id", "Issuer ID")
    "keyId" -> tr("app.projects.slug.stores_model.identity_key_id", "Key ID")
    "authType" -> tr("app.projects.slug.stores_model.identity_auth_type", "Authentication")
    "keyType" -> tr("app.projects.slug.stores_model.identity_key_type", "Key type")
    "serviceAccount" -> tr("app.projects.slug.stores_model.identity_service_account", "Service account")
    else -> key
}

/** What the store calls the thing a project binds to. */
internal fun storeAppRefLabel(platform: StorePlatform): String = when (platform) {
    StorePlatform.APP_STORE -> tr("app.projects.slug.stores_model.app_ref_apple", "Apple ID of the app")
    StorePlatform.GOOGLE_PLAY -> tr("app.projects.slug.stores_model.app_ref_play", "Package name")
}

/** Store connections a platform may bind to: the live ones, never one already removed. */
internal fun usableStoreConnections(
    connections: List<StoreConnectionDto>,
    platform: StorePlatform,
): List<StoreConnectionDto> =
    connections.filter { it.platform == platform && it.status != StoreConnectionStatus.REVOKED }

/** Every locale the server says can go, which is what a push sends when nobody narrows it. */
internal fun defaultPushSelection(diff: StoreDiffDto): Set<String> =
    diff.locales.filter { it.pushable }.map { it.storeLocale }.toSet()

/** What a push will actually send: the chosen locales the diff still calls pushable, in its order. */
internal fun pushableSelection(diff: StoreDiffDto, selected: Set<String>): List<String> =
    diff.locales.filter { it.pushable && it.storeLocale in selected }.map { it.storeLocale }

/**
 * Why Push is disabled, or null when it is not.
 *
 * A disabled button with no reason is a dead end; each branch names the next thing to do.
 */
internal fun pushBlockedReason(diff: StoreDiffDto, selected: Set<String>): String? = when {
    diff.locales.isEmpty() -> tr(
        "app.projects.slug.stores_model.blocked_empty",
        "Nothing to compare yet. Import the current listing first.",
    )
    // Everything Lingvo holds is already in the store (typically right after a push): there is nothing
    // to approve and nothing over a limit, so "not approved yet" would send someone looking for a fault.
    diff.locales.none { it.pushable } &&
        diff.locales.any { it.fields.isNotEmpty() } &&
        diff.locales.all { locale -> locale.fields.all { it.status == StoreFieldStatus.UNCHANGED } } -> tr(
        "app.projects.slug.stores_model.blocked_all_unchanged",
        "Nothing to push. The store already has all of your text.",
    )
    diff.locales.none { it.pushable } -> tr(
        "app.projects.slug.stores_model.blocked_none_pushable",
        "No language is ready to push. Every difference is either not approved yet or over a store limit.",
    )
    pushableSelection(diff, selected).isEmpty() -> tr(
        "app.projects.slug.stores_model.blocked_none_selected",
        "Choose at least one language to push.",
    )
    else -> null
}

/**
 * The sentence for a failed store request.
 *
 * The codes the store layer can raise are explained here in terms of what to do next. Everything
 * else keeps the server's own sentence — it is a message the API wrote for a person — and a
 * failure that never reached the server falls back to [fallback].
 */
internal fun storeErrorMessage(error: Throwable, fallback: String): String {
    val api = error as? ApiException ?: return fallback
    val serverMessage = api.message.takeIf(String::isNotBlank)
    return when (api.code) {
        STORE_VAULT_UNCONFIGURED -> storeVaultUnconfiguredMessage()
        STORE_DIFF_STALE -> tr(
            "app.projects.slug.stores_model.error_diff_stale",
            "The listing or the store changed since you reviewed it. The new diff is below; review it and push again.",
        )
        "store_credential_rejected" -> tr(
            "app.projects.slug.stores_model.error_credential_rejected",
            "The store rejected this key. Check that it is still active and was copied in full.",
        )
        "store_role_insufficient" -> tr(
            "app.projects.slug.stores_model.error_role_insufficient",
            "This key cannot edit the listing. Give it the App Manager role in App Store Connect, or " +
                "store listing access for this app in Play Console.",
        )
        "store_push_in_progress" -> tr(
            "app.projects.slug.stores_model.error_push_in_progress",
            "A push for this app is already running. Wait for it to finish, then refresh.",
        )
        "store_version_locked" -> serverMessage ?: tr(
            "app.projects.slug.stores_model.error_version_locked",
            "The store has no editable version right now. Create one in the store, then push again.",
        )
        "store_changed_since_push" -> serverMessage ?: tr(
            "app.projects.slug.stores_model.error_changed_since_push",
            "The store no longer holds what this push sent, so rolling back would overwrite newer text.",
        )
        "store_nothing_to_restore" -> tr(
            "app.projects.slug.stores_model.error_nothing_to_restore",
            "The store held no earlier text for these fields, so there is nothing to put back.",
        )
        "store_queued_push_stale" -> tr(
            "app.projects.slug.stores_model.error_queued_push_stale",
            "The queued text changed or is no longer approved. Review the new diff and push again.",
        )
        "store_app_ref_invalid" -> serverMessage ?: tr(
            "app.projects.slug.stores_model.error_app_ref_invalid",
            "That isn't an app id or package name the store would accept.",
        )
        "store_update_in_review" -> serverMessage ?: tr(
            "app.projects.slug.stores_model.error_update_in_review",
            "An update to this app is already in review. Nothing was changed; try again when it has been decided.",
        )
        "store_value_rejected" -> serverMessage ?: tr(
            "app.projects.slug.stores_model.error_value_rejected",
            "The store refused one of the values. Check the text it names and push again.",
        )
        "store_connection_in_use" -> tr(
            "app.projects.slug.stores_model.error_connection_in_use",
            "A project still uses this connection. Unbind its app first.",
        )
        else -> serverMessage ?: fallback
    }
}

internal fun storeVaultUnconfiguredMessage(): String = tr(
    "app.projects.slug.stores_model.error_vault_unconfigured",
    "Store connections are turned off on this server. Whoever runs it has to set LINGVO_STORE_VAULT_KEY " +
        "before a store can be connected.",
)

internal fun Throwable.isStoreCode(code: String): Boolean = (this as? ApiException)?.code == code

/** Fields in a locale that hold the whole locale back: unapproved text, or text over the store's limit. */
internal fun storeBlockedFieldCount(locale: StoreLocaleDiffDto): Int =
    locale.fields.count { it.status == StoreFieldStatus.NOT_REVIEWED || it.status == StoreFieldStatus.OVER_LIMIT }

/**
 * Fields a push would write that the store does not hold yet, in the languages chosen.
 *
 * Rollback puts back the store's earlier text, and for these there is none: the store's APIs are not
 * used to clear a field. That covers a whole new language and also a field newly filled in a language
 * the store already has, so the push confirmation counts them rather than promising an undo.
 *
 * "Does not hold" means absent, not blank: a field the store has but holds as empty text was snapshotted
 * before the push like any other, so a rollback can put that empty text back.
 */
internal fun storeIrreversibleFieldCount(diff: StoreDiffDto, chosen: List<String>): Int =
    diff.locales.filter { it.storeLocale in chosen }.sumOf { locale ->
        locale.fields.count { it.status == StoreFieldStatus.CHANGED && it.remote == null }
    }

/** What fitting did, in words: the new length, and each word it dropped and why. */
internal fun storeKeywordPlanMessage(plan: StoreKeywordPlanDto): String {
    if (!plan.changed) {
        return trf(
            "app.projects.slug.stores_model.keywords_already_fit",
            "{locale}: the keywords already fit ({bytes}/100 bytes). Nothing changed.",
            "locale" to plan.locale,
            "bytes" to plan.bytes,
        )
    }
    val dropped = buildList {
        if (plan.droppedIndexed.isNotEmpty()) {
            add(trf("app.projects.slug.stores_model.keywords_dropped_indexed", "already in the name or subtitle: {words}", "words" to plan.droppedIndexed.joinToString(", ")))
        }
        if (plan.droppedDuplicates.isNotEmpty()) {
            add(trf("app.projects.slug.stores_model.keywords_dropped_duplicates", "repeated: {words}", "words" to plan.droppedDuplicates.joinToString(", ")))
        }
        if (plan.droppedOverBudget.isNotEmpty()) {
            add(trf("app.projects.slug.stores_model.keywords_dropped_budget", "over the budget: {words}", "words" to plan.droppedOverBudget.joinToString(", ")))
        }
    }
    return trf(
        "app.projects.slug.stores_model.keywords_fitted",
        "{locale}: keywords fitted to {bytes}/100 bytes and saved as a draft to approve. Dropped {dropped}.",
        "locale" to plan.locale,
        "bytes" to plan.bytes,
        "dropped" to dropped.joinToString("; "),
    )
}

internal fun storeIdentityValue(key: String, value: String): String = when {
    key == "authType" && value == "managed" -> tr("app.projects.slug.stores_model.identity_managed", "Keyless workspace account")
    key == "authType" && value == "json_key" -> tr("app.projects.slug.stores_model.identity_json", "Advanced JSON key")
    key == "keyType" && value == "individual" -> tr("app.projects.slug.stores_model.identity_individual", "Individual key")
    key == "keyType" && value == "team" -> tr("app.projects.slug.stores_model.identity_team", "Team key")
    else -> value
}
